import React, { useState, useRef } from 'react';
import { Formik, Form, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import ConfirmationPopup from './ConfrimationPopup';
import SuccessPopup from './SuccessModal';
import { useSelector, useDispatch } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import rollbackRequest from '@custom/rtk-apis/report/RollBackrequest';
import { useNavigate } from 'react-router-dom';
// import './PopUp.css';
import { SuccessModal, useTheme } from '@ohif/ui';
import { RollbackModalProps } from '@custom/Utils/constants/interfaces';

const RollbackModal: React.FC<RollbackModalProps> = ({ isOpen, onClose, reportid }) => {

  const dispatch: AppDispatch = useDispatch();

  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  // Access the rollback state from Redux
  const { loading, success, error } = useSelector((state: RootState) => state.Rollbackresponce);

  const [formValues, setFormValues] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isError, setIsError] = useState(false);

  const statusOptions = [
    { value: "", label: "-Select-" },
    { value: "Draft", label: "Draft" },
    { value: "Completed", label: "Completed" },
    { value: "Report Entry", label: "Report Entry" }
  ]

  const initialValues = {
    reason: '',
    explanation: '',
    status: '',
  };

  const validationSchema = Yup.object().shape({
    reason: Yup.string().required('Reason is required.'), // Validation for single selection
    explanation: Yup.string().required('Explanation is required.'),
    status: Yup.string().required('Please select a status.'),
  });

  const handleSave = (values: any) => {
    setFormValues(values);
    onClose();
    setIsConfirmationOpen(true);
  };


  const handleConfirm = () => {
    if (formValues) {
      const rollbackData = {
        _id: reportid,
        rollbackReason: formValues.reason,
        rollbackNotes: formValues.explanation,
        rollbackStatus: formValues.status,
      };

      dispatch(rollbackRequest(rollbackData))
        .unwrap()
        .then((response) => {
          if (response.status === 'SUCCESS') {
            setSuccessMessage(response.msg || 'An error occurred while processing your request.');
            setIsError(true);
          } else {
            setSuccessMessage(response.msg || 'Request processed successfully.');
            setIsError(false);
          }
          setIsSuccessOpen(true);

          // Automatically close the success modal after 5 seconds and then navigate
          timeoutRef.current = setTimeout(() => {
            setIsSuccessOpen(false);
            // Navigate to /reportlist after the modal closes
            navigate('/reportlist');
          }, 1000);
        })
        .catch((err) => {
          console.log('Error object:', err);

          const errorMessage = err?.response?.data?.msg || err?.msg || 'An unexpected error occurred.';
          setSuccessMessage(errorMessage);
          setIsError(true);
          setIsSuccessOpen(true);

          // Automatically close the success modal after 5 seconds
          timeoutRef.current = setTimeout(() => {
            setIsSuccessOpen(false);
          }, 5000);
        });

      setIsConfirmationOpen(false); // Close confirmation popup
    }
  };

  const handleCancel = () => {
    setIsConfirmationOpen(false);
  };

  const handleCloseSuccessPopup = () => {
    setIsSuccessOpen(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  };

  if (!isOpen && !isConfirmationOpen && !isSuccessOpen) return null;

  return (
    <>
      {/* Rollback Modal */}
      {isOpen && (
        <div className="modal-overlay popup-overlay fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center" style={{ zIndex: 9999 }}>
          <div className="modal-container  bg-white p-6 rounded-lg shadow-lg" style={{width:"40rem"}}>
            <div className="flex justify-between border-b pb-4">
              <h2 className="modal-heading popup-heading-text" style={{ color: isDarkMode ? '#fff' : '#000' }}>Rollback Reason!</h2>
              <div className="text-xl cursor-pointer" onClick={onClose}>X</div>
            </div>

            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSave} // Trigger confirmation popup on form submit
            >
              {({
                values,
                errors,
                handleChange,
                handleBlur,
                touched,
                setFieldValue,
              }) => (
                <Form>
                  <div className="mt-4">
                    <div className="mb-4">
                      <label className="text-left modal-subheading" style={{ color: isDarkMode ? '#fff' : '#000' }}>Reason for Rollback?</label>
                      <div className="flex flex-col space-y-2 mt-2" >
                        <label className='label-text-font' style={{ color: isDarkMode ? '#fff' : '#000' }}>
                          <input
                            type="checkbox"
                            name="reason"
                            value="Correction"
                            checked={values.reason === 'Correction'}
                            onChange={() => setFieldValue('reason', 'Correction')}
                            onBlur={handleBlur}
                          /> Correction
                        </label>
                        <label className='label-text-font' style={{ color: isDarkMode ? '#fff' : '#000' }}>
                          <input
                            type="checkbox"
                            name="reason"
                            value="Addition"
                            checked={values.reason === 'Addition'}
                            onChange={() => setFieldValue('reason', 'Addition')}
                            onBlur={handleBlur}
                          /> Addition
                        </label>
                        <label className='label-text-font' style={{ color: isDarkMode ? '#fff' : '#000' }}>
                          <input
                            type="checkbox"
                            name="reason"
                            value="Others"
                            checked={values.reason === 'Others'}
                            onChange={() => setFieldValue('reason', 'Others')}
                            onBlur={handleBlur}
                          /> Others
                        </label>
                        {touched.reason && errors.reason && (
                          <ErrorMessage name="reason" component="div" className="error text-error" />
                        )}
                      </div>
                    </div>

                    <div className="mb-4">
                      <label className="text-left label-text-font" style={{ color: isDarkMode ? '#fff' : '#000' }}>Please Explain Further<span style={{ color: '#F04438' }}>*</span></label>
                      <textarea
                        name="explanation"
                        className="p-2 border rounded w-full text-font"
                        placeholder="Enter your note here..."
                        onChange={handleChange}
                        onBlur={handleBlur}
                        style={{ height: '100px' }}
                      />
                      {touched.explanation && errors.explanation && (
                        <ErrorMessage name="explanation" component="div" className="error text-error" />
                      )}
                    </div>

                    <div className="mb-6">
                      <label className="text-left label-text-font" style={{ color: isDarkMode ? '#fff' : '#000' }}>Rollback Status</label>
                      <select
                        name="status"
                        className="p-2 border rounded w-full select-text"
                        onChange={handleChange}
                        onBlur={handleBlur}

                      >
                        {statusOptions.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      {touched.status && errors.status && (
                        <ErrorMessage name="status" component="div" className="error text-error" />
                      )}
                    </div>

                    <div className="modal-actions flex justify-start gap-4">
                      <button type="submit" className="custom-button main-theme save-btn">Save</button>
                      <button type="button" className="custom-button popup-button" onClick={onClose}>Cancel</button>
                    </div>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      )}

      {/* Confirmation Popup */}
      {isConfirmationOpen && (
        <ConfirmationPopup
          isOpen={isConfirmationOpen}
          onConfirm={handleConfirm}
          onCancel={handleCancel}
        />
      )}

      {/* Success Popup */}
      {isSuccessOpen && (
        <SuccessModal
          isSuccessModalOpen={isSuccessOpen}
          title={successMessage}
          handleCloseModal={handleCloseSuccessPopup}
        />
      )}
    </>
  );
};

export default RollbackModal;
